import { ApiError, type ApiOpts } from '@/lib/api';
import type {
  StudyAnalysisReviewItem,
  StudyPracticeAttemptResult,
  StudyPracticeOption,
} from '@/pages/learn/study/studyAnalysisFollowUpApi';
import type {
  AssessmentDraftQuestion,
  SelfAssessmentConfig,
  SelfAssessmentPaper,
} from '@/pages/learn/study/studySelfAssessmentApi';
import { DEMO_ANALYSIS_ID, DEMO_WORKBOOK_ID } from './demoLearningOutcomes';
import {
  DEMO_ASSESSMENT_ID,
  DEMO_ASSESSMENT_PRESETS,
  demoAssessmentPaper,
  demoAssessmentQuestions,
} from './demoAssessments';
const denied = () =>
  new ApiError(
    403,
    'DEMO_ASSESSMENT_ONLY',
    'This demo uses prepared assessments and fixed answer examples. No AI request was sent.',
  );
const conflict = () =>
  new ApiError(
    409,
    'STUDY_ASSESSMENT_REVISION_CONFLICT',
    'Reopen this assessment before changing it.',
  );
const fields = (v: unknown) => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {});
const normal = (s: string) => s.toLowerCase().replace(/\s+/g, '').replace(/×/g, '*');
const signature = (config: SelfAssessmentConfig) => JSON.stringify(config);
export function createDemoAssessmentAdapter(reviews: StudyAnalysisReviewItem[]) {
  const papers = new Map<string, SelfAssessmentPaper>([
    [DEMO_ASSESSMENT_ID, demoAssessmentPaper()],
  ]);
  const keys = new Map<string, AssessmentDraftQuestion[]>([
    [DEMO_ASSESSMENT_ID, demoAssessmentQuestions('mixed')],
  ]);
  const requests = new Map<string, { fingerprint: string; result: unknown }>();
  const addReview = (id: string, questionId: string, title: string) => {
    if (!reviews.some((r) => r.practice_set_id === id && r.question_id === questionId))
      reviews.push({
        kind: 'analysis',
        self_assessment: true,
        id: `assessment-review-${id}-${questionId}`,
        analysis_id: DEMO_ANALYSIS_ID,
        document_id: DEMO_WORKBOOK_ID,
        document_name: 'Alex’s prepared mathematics assessment',
        question_id: questionId,
        question_number: questionId.slice(2),
        practice_set_id: id,
        topic: title,
        subject: { subject_key: 'mathematics', display_name: 'Mathematics' },
        source: 'practice_wrong',
        created_at: '2026-10-17T05:00:00.000Z',
        review_due_at: '2026-10-17T05:00:00.000Z',
        due: true,
      });
  };
  addReview(DEMO_ASSESSMENT_ID, 'pq2', papers.get(DEMO_ASSESSMENT_ID)!.assessment.title);
  const requirePaper = (id: string) => {
    const row = papers.get(id);
    if (!row)
      throw new ApiError(404, 'STUDY_ASSESSMENT_NOT_FOUND', 'This assessment is not in this demo.');
    return row;
  };
  const matchPreset = (v: unknown) =>
    DEMO_ASSESSMENT_PRESETS.find((p) => signature(p.config) === JSON.stringify(v));
  return (path: string, opts: ApiOpts): unknown => {
    const u = new URL(path, 'https://demo.invalid'),
      method = opts.method ?? 'GET',
      b = fields(opts.body);
    if (u.pathname === '/study/self-assessments' && method === 'GET')
      return {
        items:
          u.searchParams.get('subject_key') === 'mathematics'
            ? [...papers.values()].map(({ draft_questions: _draft, ...paper }) => paper)
            : [],
        limit: 50,
      };
    const make = /^\/study\/analyses\/demo-maths-reading\/practice-sets(?:\/(quote))?$/.exec(
      u.pathname,
    );
    if (make && method === 'POST' && b.assessment) {
      const preset = matchPreset(b.assessment);
      if (!preset) throw denied();
      if (make[1])
        return {
          assessment: preset.config,
          stars: 0,
          question_count: preset.config.question_count,
          check_stars_per_attempt: 0,
        };
      if (b.expected_stars !== 0 || typeof b.idempotency_key !== 'string') throw denied();
      const fingerprint = JSON.stringify([u.pathname, b.assessment]),
        prior = requests.get(b.idempotency_key);
      if (prior) {
        if (prior.fingerprint !== fingerprint) throw denied();
        return requirePaper((prior.result as SelfAssessmentPaper).id);
      }
      if (papers.size >= 30) throw denied();
      const paper = demoAssessmentPaper(preset.key, crypto.randomUUID(), false);
      papers.set(paper.id, paper);
      keys.set(paper.id, demoAssessmentQuestions(preset.key));
      requests.set(b.idempotency_key, { fingerprint, result: structuredClone(paper) });
      return paper;
    }
    const record =
      /^\/study\/analyses\/demo-maths-reading\/self-assessments\/([^/]+)(?:\/(start))?$/.exec(
        u.pathname,
      );
    if (record) {
      const paper = requirePaper(record[1]);
      if (method === 'GET' && !record[2]) return paper;
      if (method === 'POST' && record[2] === 'start') {
        if (paper.phase === 'published') return paper;
        if (b.expected_revision !== paper.revision) throw conflict();
        paper.phase = 'published';
        paper.revision++;
        delete paper.draft_questions;
        return paper;
      }
      if (method === 'PUT' && !record[2]) {
        if (paper.phase !== 'draft' || b.expected_revision !== paper.revision) throw conflict();
        const rows = b.questions as AssessmentDraftQuestion[];
        if (
          typeof b.title !== 'string' ||
          !b.title.trim() ||
          b.title.length > 160 ||
          !Array.isArray(rows) ||
          rows.length !== paper.assessment.question_count ||
          new Set(rows.map((q) => q.stem?.trim())).size !== rows.length ||
          rows.some(
            (q) =>
              !q.stem?.trim() ||
              !q.answer?.trim() ||
              !q.worked_solution?.trim() ||
              !paper.assessment.question_kinds.includes(q.kind) ||
              (q.kind === 'multiple_choice' &&
                (!q.options ||
                  q.options.length !== 4 ||
                  new Set(q.options).size !== 4 ||
                  !['A', 'B', 'C', 'D'].includes(q.correct_option ?? ''))),
          )
        )
          throw denied();
        const updated = rows.map((q, i) => ({ ...q, id: `pq${i + 1}` }));
        paper.assessment.title = b.title.trim();
        paper.revision++;
        keys.set(paper.id, updated);
        paper.draft_questions = updated;
        paper.questions = updated.map((q) => ({
          id: q.id,
          kind: q.kind,
          stem: q.stem,
          options: q.options,
          attempts: 0,
          answer_available: false,
          answer: null,
          latest_attempt: null,
        }));
        return paper;
      }
      throw denied();
    }
    const attempt =
      /^\/study\/analyses\/demo-maths-reading\/practice-sets\/([^/]+)\/questions\/(pq\d+)\/attempts$/.exec(
        u.pathname,
      );
    if (attempt && papers.has(attempt[1])) {
      if (method !== 'POST') throw denied();
      const paper = requirePaper(attempt[1]);
      if (paper.phase !== 'published') throw conflict();
      const question = paper.questions.find((q) => q.id === attempt[2]),
        answer = keys.get(paper.id)?.find((q) => q.id === attempt[2]);
      if (!question || !answer) throw denied();
      const submitted = question.kind === 'multiple_choice' ? b.choice : b.answer_text;
      if (
        typeof submitted !== 'string' ||
        !submitted.trim() ||
        submitted.length > 2000 ||
        b.expected_stars !== 0 ||
        typeof b.idempotency_key !== 'string' ||
        (question.kind === 'multiple_choice' && !['A', 'B', 'C', 'D'].includes(submitted))
      )
        throw denied();
      const fingerprint = JSON.stringify([u.pathname, submitted]),
        prior = requests.get(b.idempotency_key);
      if (prior) {
        if (prior.fingerprint !== fingerprint) throw denied();
        return prior.result;
      }
      const correct =
        question.kind === 'multiple_choice'
          ? submitted === answer.correct_option
          : normal(submitted) ===
            normal(question.kind === 'working' ? answer.worked_solution : answer.answer);
      const verdict = correct
        ? 'correct'
        : question.kind === 'multiple_choice'
          ? 'incorrect'
          : 'cant_tell';
      question.attempts++;
      question.answer_available = true;
      question.answer = {
        answer: answer.answer,
        correct_option: answer.correct_option as StudyPracticeOption | null,
        worked_solution: answer.worked_solution,
      };
      question.latest_attempt = {
        id: crypto.randomUUID(),
        question_id: question.id,
        practice_set_id: paper.id,
        status: 'checked',
        verdict,
        hint:
          verdict === 'cant_tell'
            ? 'Demo only: this text is not a prepared answer. No AI evaluated it. Compare the worked example or use its prepared sample.'
            : correct
              ? 'Demo answer-key comparison matches the prepared reference.'
              : 'Demo answer-key comparison: check all equal parts and both conditions.',
        student_answer_text: submitted,
        stars: 0,
        stars_refunded: 0,
        refunded: false,
        error_code: null,
        created_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
      };
      if (verdict === 'incorrect') addReview(paper.id, question.id, paper.assessment.title);
      if (correct) {
        const i = reviews.findIndex(
          (r) => r.practice_set_id === paper.id && r.question_id === question.id,
        );
        if (i >= 0) reviews.splice(i, 1);
      }
      const result: StudyPracticeAttemptResult = {
        practice_set_id: paper.id,
        attempt: question.latest_attempt,
        question,
      };
      requests.set(b.idempotency_key, { fingerprint, result: structuredClone(result) });
      return result;
    }
    return undefined;
  };
}
